import { ECustomFieldType, EParseType } from '../enums'
import { exhaustiveCheck } from '../helpers'
import { TParserSettingItem } from '../types/commonTypes'
import { TOptions } from '../types/fieldDefinition'

type TCommonProps = {
  type: ECustomFieldType.TEXT | ECustomFieldType.NUMBER | ECustomFieldType.EMAIL | ECustomFieldType.DATE
}
type TPhoneProps = {
  type: ECustomFieldType.PHONE_NUMBER
  countryCode: string
}
type TSelectProps = {
  type: ECustomFieldType.SINGLE_SELECT | ECustomFieldType.MULTI_SELECT
  selections: TOptions
}

type TParsableField = {
  key: string
  label: string
} & (TCommonProps | TPhoneProps | TSelectProps)

function parseExternalId(externalId: string, parsableFields: TParsableField[], parserSettings: TParserSettingItem[]) {
  const toArray = (modelValue: string | string[]) => (typeof modelValue === 'string' ? [modelValue] : modelValue)
  const selectionLabel2key = (field: TSelectProps, selectionLabels: string[]) =>
    selectionLabels.map(
      (label) => Object.entries(field.selections ?? []).find((selection) => selection[1].label === label)?.[0] ?? ''
    )

  const parse = (parseStr: string, setting: TParserSettingItem): string | string[] => {
    switch (setting.parserType) {
      case EParseType.REGEX: {
        const parsedResults = parseStr.match(new RegExp(setting.regexRule, 'g')) ?? []
        return setting.index ? parsedResults[(setting.index ?? 1) - 1] ?? [] : parsedResults
      }
      case EParseType.LENGTH:
        return parseStr.substring(setting.startPosition, setting.endPosition)
      case EParseType.DELIMITER:
        return parseStr.split(setting.delimiter)[setting.index - 1] ?? ''
      default:
        exhaustiveCheck(setting)
    }
  }

  const isSelectField = (field: TCommonProps | TPhoneProps | TSelectProps): field is TSelectProps =>
    [ECustomFieldType.SINGLE_SELECT, ECustomFieldType.MULTI_SELECT].includes(field?.type)

  const parsedFields: Record<string, string | string[]> = {}
  parserSettings.forEach((setting) => {
    const fieldPath = setting.targetFieldPath
    const field = parsableFields.find((f) => f.key === fieldPath)!
    const modelValue = parse(externalId, setting)

    if (isSelectField(field)) {
      if (field?.type === ECustomFieldType.SINGLE_SELECT) {
        parsedFields[fieldPath] = selectionLabel2key(field, toArray(modelValue))[0]
      } else if (field?.type === ECustomFieldType.MULTI_SELECT) {
        parsedFields[fieldPath] = selectionLabel2key(field, toArray(modelValue))
      }
    } else {
      parsedFields[fieldPath] = Array.isArray(modelValue) ? modelValue[0] : modelValue
    }
  })

  return parsedFields
}

export { parseExternalId, TParsableField, TCommonProps, TPhoneProps, TSelectProps }
